import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { config } from '../../constants'
import MainModule from '../../modules/main'
import { getContentTypeFromUrl, getSlotContent, isEventPageType } from '../../utils/gtm/utilities'

export interface LvcCard {
  href: string
  isShowcase: boolean
  isHovered: boolean
}

export class LvcCard extends MinzeElement {
  attrs: Attrs = [
    ['is-showcase', false],
    ['href', '#'],
  ]
  reactive: Reactive = [['isHovered', false]]

  onClick = async (event: Event) => {
    event.preventDefault()

    const clickedEl = event.target as HTMLElement
    const tagNamesToIgnore = [
      'LVC-FAVORITE-ICON-BUTTON',
      'LVC-EVENT-TICKET',
      'LVC-TAGS',
      'LVC-EVENT-SHOW-MORE',
      'LVC-GOOGLE-MAPS',
    ]

    if (!tagNamesToIgnore.includes(clickedEl.tagName)) {
      const pathName = window.location.pathname

      // Only track trigger GTM event if the link is an event page type.
      try {
        if (isEventPageType(this.href)) {
          const eventTitle = getSlotContent(this, 'title') || 'Unknown Event'
          const fullUrl = new URL(this.href, window.location.origin).toString()
          await MainModule.getInstance().analyticsTrack('vers_page_evenement', {
            link_url: fullUrl,
            link_text: eventTitle,
            emplacement: getContentTypeFromUrl(pathName),
          })
        }
      } catch (error) {
        console.error('Error tracking event click:', error)
      }
      window.location.href = this.href
    }
  }

  handleMouseOver = () => {
    this.isHovered = true
  }

  handleMouseLeave = () => {
    this.isHovered = false
  }

  html = () => `
    <a href="${this.href}" class="card ${this.isShowcase ? 'is-showcase' : ''}">
      <slot name="favorite"></slot>
      <slot name="status"></slot>
      <div class="card-image-wrapper">
        <slot name="festival-icon"></slot>
        <slot name="image"></slot>

        ${
          this.isShowcase
            ? /*html*/ `
          <slot name="title"></slot>
        `
            : ''
        }

        <div class="image-overlay"></div>
      </div>
      
      ${
        !this.isShowcase
          ? /*html*/ `
        <slot name="ticket"></slot>
        <slot name="title"></slot>
        <slot name="festival"></slot>
        `
          : ''
      }
      
      <div class="card-content-wrapper">
        <div class="card-text-wrapper">
          ${
            this.isShowcase
              ? `
            <slot name="festival"></slot>
            `
              : ''
          }
          <slot name="date"></slot>
          <slot name="location"></slot>
          <slot name="tag"></slot>
        </div>

        ${
          this.isShowcase
            ? /*html*/ `
          <slot name="ticket"></slot>
          `
            : ''
        }
        
      </div>
    </a>
  `
  css = () => /*css*/ `
    :host {
      height: 100%;
      ${this.isShowcase ? 'grid-column: span 2;' : ''}
      color: #1E007D;
      transition: transform 0.2s ease;
      font-size: 0.875rem;
      position: relative !important;

    }
    .card {
      text-decoration: none;
      color: #1E007D;
    }

    .card-content-wrapper {
      margin-top: 1rem;
      display: flex;
      align-items: start;
      flex-direction: ${this.isShowcase ? 'row' : 'column'};
    }

    .card-text-wrapper {
      gap: 4px;
      align-items: start;
      display: flex;
      flex-direction: column;
    }

    .card-image-wrapper {
      margin-bottom: 1.25rem;
      position: relative;
      aspect-ratio: 16/9;
      overflow: hidden;
      border-radius: 18px;
    }

    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      -webkit-mask-image: url(${config.images.globalMask});
      mask-image: url(${config.images.globalMask});
      -webkit-mask-size: cover;
      mask-size: cover;
      width: 100%;
      aspect-ratio: 16/9;
      background-color: ${this.isHovered ? `#1E007D99` : this.isShowcase ? `rgba(0, 0, 0, 0.35)` : ''};
      transition: background-color 0.2s ease;
    }
    slot[name="festival"]::slotted(*) {
      margin-top: ${this.isShowcase ? '-12px' : '6px'};
      margin-bottom: ${this.isShowcase ? '2px' : '-10px'};
    }
    slot[name="status"]::slotted(*) {
      position: absolute;
      top: calc(-67px / 2);
      left: 16px;
      z-index: 2;
    }
    slot[name="favorite"]::slotted(lvc-favorite-icon-button) {
      z-index: 2;
      position: absolute !important;
      top: 10px !important;
      right: 10px !important
    }

    slot[name="ticket"]::slotted(*) {
      margin-left: auto;
      margin-bottom: 1.25rem;
      height: 38px;
    }
    slot[name="tag"]::slotted(*) {
      font-size: 12px;
    }
    slot[name="date"]::slotted(*) {
      font-weight: 700;
    }
    slot[name="title"]::slotted(*) {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      font-size: 1.5rem;
      font-weight: 800;
      text-transform: uppercase;
      color: #1E007D;
      line-height: 1.1;
      ${
        this.isShowcase
          ? `
        position: absolute;
        z-index: 1;
        padding: 25px;
        bottom: 0;
        color: white;
      `
          : ''
      }
    }
    slot[name="festival-icon"]::slotted(*) {
      position: absolute;
      top: ${this.isShowcase ? '16px' : '8px'};
      left: ${this.isShowcase ? '16px' : '8px'};
      z-index: 2;
      width: ${this.isShowcase ? '72px' : '60px'};
      height: ${this.isShowcase ? '72px' : '60px'};
    }
    slot[name="image"]::slotted(img) {
      overflow: hidden;
      width: 100%;
      aspect-ratio: 16/19;
      object-fit: cover;
    }
  `
  eventListeners?: EventListeners | undefined = [
    ['a', 'click', this.onClick],
    [this, 'mouseover', this.handleMouseOver],
    [this, 'mouseleave', this.handleMouseLeave],
  ]
}
